import React from 'react';
import clsx from 'clsx';
import { MinimalUser, SalesfloorParticipant } from 'sb-lib/types';
import { Client, OrgInvite, Agent, CrmContactSearchResult } from 'types';
import { AvatarSize, makeAvatarUrl } from 'shared/utils/avatar';
import EmptyAvatar from 'shared/components/EmptyAvatar';
import defaultUserImg from 'shared/assets/images/avatar-default.png';
import { BOT_AVATAR } from 'shared/utils/constants';
import './style.less';
import { AIPersona } from 'types/ai-persona';

export type UserAvatarType =
  | Agent
  | MinimalUser
  | Client
  | CrmContactSearchResult
  | SalesfloorParticipant
  | OrgInvite
  | { bot: true }
  | (AIPersona & { ai: true })
  | null
  | undefined;

interface Props {
  user: UserAvatarType;
  size: number;
  className?: string;
}

const avatarSizes: AvatarSize[] = [40, 80, 160, 400];

const getUserProps = (user: UserAvatarType) => {
  // (Widget) Agent
  if (user && 'name' in user && !('ai' in user)) {
    return {
      imageUrl: user.avatar,
      displayName: user.name,
      id: user.id,
    };
  }

  // MinimalUser
  if (user && 'avatar' in user) {
    return {
      imageUrl: user.avatar?.imageUrl,
      displayName: user.displayName,
      id: user.id,
    };
  }

  // Client
  if (user && 'helpedBy' in user) {
    return {
      imageUrl: user.helpedByAvatar,
      displayName: user.helpedByName,
      id: user.helpedBy,
    };
  }

  // SalesfloorParticipant
  if (user && 'avatarUrl' in user && 'id' in user && !('ai' in user)) {
    return {
      imageUrl: user.avatarUrl,
      displayName: user.displayName,
      id: user.id,
    };
  }

  // CrmContactSearchResult
  if (user && 'avatarUrl' in user && 'email' in user) {
    return {
      imageUrl: user.avatarUrl,
      displayName: user.displayName,
      id: user.email,
    };
  }

  // OrgInvite > User
  if (user && 'user' in user && user.user) {
    return {
      imageUrl: user.user?.avatar?.imageUrl,
      displayName: user.user?.displayName,
      id: user.user?.id,
    };
  }

  // OrgInvite
  if (user && 'emailAddress' in user) {
    return {
      imageUrl: '',
      displayName: user.emailAddress,
      id: user.emailAddress,
    };
  }

  // Bot
  if (user && 'bot' in user) {
    return {
      imageUrl: BOT_AVATAR,
      displayName: 'Rep.ai Bot',
      id: -1,
    };
  }

  // AIPersona
  if (user && 'ai' in user) {
    return {
      imageUrl: user.avatarUrl,

      displayName: user.name,
      id: user.id,
    };
  }

  return { imageUrl: '', displayName: '', id: 0 };
};

const UserAvatar: React.FC<Props> = ({ user, size, className }) => {
  const { imageUrl, displayName, id } = getUserProps(user);

  if (imageUrl) {
    const imgSize =
      avatarSizes.find(aSize => size <= aSize) || avatarSizes[avatarSizes.length - 1];

    return (
      <img
        className={clsx('UserAvatar', className)}
        src={makeAvatarUrl(imageUrl, imgSize)}
        alt={displayName}
      />
    );
  }

  if (id && displayName) {
    return <EmptyAvatar className={className} id={id} name={displayName} size={size} />;
  }

  return <img className={clsx('UserAvatar', className)} src={defaultUserImg} />;
};

export default UserAvatar;
